/**
 * ADR-0013 "Defaults per business type". The server is the source of truth and applies these; the web
 * keeps a copy only to tell the owner what "Terapkan pengaturan awal" will change before they confirm.
 */
import type { BusinessType, CashierSettings } from './api/endpoints'
import type { ReceiptLayoutSettings } from './receiptLayout'

// The lock screen option, the rights of the roles and the receipt settings are not part of a business profile.
type CashDrawerKey = 'cash_drawer_enabled' | 'default_opening_cash' | 'business_day_start' | 'business_day_end' | 'show_demo_data'
type Defaults = Omit<CashierSettings, 'business_type' | 'lock_show_users' | 'role_permissions' | 'receipt_digital_enabled' | 'receipt_copies' | CashDrawerKey | keyof ReceiptLayoutSettings>

const common = {
  tax_enabled: false,
  tax_rate_bps: 1000,
  service_enabled: false,
  service_rate_bps: 500,
  prices_include_tax: false,
  takeaway_fee_amount: 0,
  kitchen_ticket_copies: 1,
  receipt_show_notes: false,
  open_bill_enabled: false,
} as const

// A shop and a general till start plain: no staff, tables or kitchen (ADR-0026).
const plain = {
  ...common,
  staff_per_line_enabled: false,
  rounding_unit: 0,
  order_type_enabled: false,
  table_number_enabled: false,
  table_required: false,
  table_list: [],
  item_notes_enabled: false,
  note_presets: [],
  kitchen_ticket_enabled: false,
} as const

export const PROFILE_DEFAULTS: Record<BusinessType, Defaults> = {
  retail: { ...plain, table_list: [], note_presets: [] },
  other: { ...plain, table_list: [], note_presets: [] },
  services: {
    ...common,
    staff_per_line_enabled: true,
    rounding_unit: 0,
    order_type_enabled: false,
    table_number_enabled: false,
    table_required: false,
    table_list: [],
    item_notes_enabled: false,
    note_presets: [],
    kitchen_ticket_enabled: false,
  },
  food: {
    ...common,
    staff_per_line_enabled: false,
    rounding_unit: 100,
    order_type_enabled: true,
    table_number_enabled: true,
    table_required: false,
    table_list: [],
    item_notes_enabled: true,
    note_presets: ['Tidak pedas', 'Pedas', 'Kuah pisah', 'Tanpa sayur', 'Bungkus terpisah'],
    kitchen_ticket_enabled: true,
  },
}

export const MAX_PRESETS = 10
export const MAX_PRESET_LENGTH = 30
export const MAX_TABLES = 200
export const MAX_TABLE_LENGTH = 12

/** A table name as the server accepts it: letters, digits, spaces and hyphens, 1-12 characters. Null if it is not one. */
export function cleanTableName(raw: string): string | null {
  const name = raw.split(/\s+/).filter(Boolean).join(' ')
  return name.length >= 1 && name.length <= MAX_TABLE_LENGTH && /^[A-Za-z0-9][A-Za-z0-9 -]*$/.test(name) ? name : null
}

/** The list with `1` to `count` added (the ones already there are skipped), never beyond the limit. */
export function addTableRange(list: string[], count: number): string[] {
  const have = new Set(list.map((x) => x.toLowerCase()))
  const next = [...list]
  for (let n = 1; n <= count && next.length < MAX_TABLES; n++) {
    if (!have.has(String(n))) next.push(String(n))
  }
  return next
}

/** The settings that differ between a current outlet and a profile's defaults, in display order. */
export function profileChanges(current: CashierSettings, target: BusinessType): (keyof Defaults)[] {
  const wanted = PROFILE_DEFAULTS[target]
  return (Object.keys(wanted) as (keyof Defaults)[]).filter((k) => JSON.stringify(current[k]) !== JSON.stringify(wanted[k]))
}
